import { useEffect, useRef } from 'react';
import { Button, Input, Label } from '@shared/components';
import { CODE_ACCEPTED_MESSAGE } from '../utils/fundingApprovalCopy';
import { isCompleteCode } from '../utils/fundingCode';
import { PanelLinkAction } from './PanelLinkAction';
import type { CodeCheckStatus } from '../utils/fundingCodeCheck';

interface Props {
    code: string;
    onCodeChange: (raw: string) => void;
    hint: string;
    error?: string | null;
    onResend: () => void;
    onSendToSomeoneElse: () => void;
    sending: boolean;
    checkStatus: CodeCheckStatus;
    onCheck: () => void;
}

const FIELD_ID = 'funding-approval-code';
const HINT_ID = 'funding-approval-code-hint';
const ERROR_ID = 'funding-approval-code-error';

export function FundingCodeEntry({
    code,
    onCodeChange,
    hint,
    error = null,
    onResend,
    onSendToSomeoneElse,
    sending,
    checkStatus,
    onCheck,
}: Props) {
    const wrapper = useRef<HTMLDivElement>(null);
    const acceptedLine = useRef<HTMLParagraphElement>(null);
    const checkedFromButton = useRef(false);
    const checking = checkStatus === 'checking';
    const complete = isCompleteCode(code);

    useEffect(() => {
        if (checkStatus === 'rejected' && error) wrapper.current?.querySelector<HTMLInputElement>(`#${FIELD_ID}`)?.focus();
    }, [checkStatus, error]);

    useEffect(() => {
        if (checkStatus !== 'accepted' || !checkedFromButton.current) return;
        checkedFromButton.current = false;
        const focusLost = document.activeElement === null || document.activeElement === document.body;
        if (focusLost) acceptedLine.current?.focus();
    }, [checkStatus]);

    return (
        <div ref={wrapper}>
            <Label htmlFor={FIELD_ID} required>
                Approval code
            </Label>
            <Input
                id={FIELD_ID}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={code}
                onChange={(event) => onCodeChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    if (!complete || checking) return;
                    checkedFromButton.current = false;
                    onCheck();
                }}
                error={error !== null}
                aria-invalid={error !== null}
                aria-describedby={error ? `${ERROR_ID} ${HINT_ID}` : HINT_ID}
            />
            {error && (
                <p id={ERROR_ID} role="alert" className="mt-1.5 text-xs text-mp-error">
                    {error}
                </p>
            )}
            <p id={HINT_ID} className="mt-1.5 text-xs text-mp-muted">
                {hint}
            </p>
            {checkStatus === 'accepted' ? (
                <p
                    ref={acceptedLine}
                    role="status"
                    tabIndex={-1}
                    className="mt-2 text-sm text-mp-success"
                >
                    {CODE_ACCEPTED_MESSAGE}
                </p>
            ) : (
                <Button
                    type="button"
                    size="sm"
                    className="mt-2 w-full"
                    ariaDisabled={!complete || checking}
                    ariaDescribedBy={HINT_ID}
                    onClick={() => {
                        checkedFromButton.current = true;
                        onCheck();
                    }}
                >
                    {checking ? 'Checking...' : 'Check code'}
                </Button>
            )}
            <div className="mt-1.5 flex flex-col gap-1.5">
                <PanelLinkAction ariaDisabled={sending} onClick={onResend}>
                    Send a new code
                </PanelLinkAction>
                <PanelLinkAction ariaDisabled={sending} onClick={onSendToSomeoneElse}>
                    Send to someone else
                </PanelLinkAction>
            </div>
        </div>
    );
}
